import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin } from 'antd';
import useCredentialProviders from 'hooks/credentialing/useCredentialProviders';
import { useGroupCredentialingEnrollment } from 'hooks/credentialing/useGroupCredentialingEnrollment';
import AddProvidersStep from 'pages/GroupCredentialingPage/components/AddProvidersStep';
import FlowShell from 'pages/GroupCredentialingPage/components/FlowShell';
import { TCredentialingProvider } from 'types/credentialing';
import { PATH } from 'constants/path';

// Standalone page for adding providers to an *existing* practice (post-onboarding). Unlike the
// new-practice flow, there's no practice enrollment or owner question — the practice already exists.
// The practice payers block is shown (already selected) so the owner sees what new providers inherit.
const AddCredentialingProvidersPage: React.FC = () => {
    const navigate = useNavigate();

    // Fetch real data from backend, same sources as the main group-credentialing flow.
    const [enrollmentData, enrollmentLoading] = useGroupCredentialingEnrollment();
    const [roster, providersLoading, refetchCredentialProviders] = useCredentialProviders();

    const handleExit = () => navigate(-1);
    const handleBack = () => navigate(-1);
    const handleAddToPractice = () => {
        navigate(`${PATH.CREDENTIALING_START}?mode=group-review`);
    };

    const isDone = (p:TCredentialingProvider) => p.onboardingStatus === 'COMPLETED'
        || (p.onboardingStatus === 'IN_PROGRESS' && p.onboardingStep === 'PAYERS_SELECTED');
    const addDisabled = roster.length === 0 || roster.some((p) => !isDone(p)) || roster.every((p) => p.onboardingStatus === 'COMPLETED');;

    const pageLoading = enrollmentLoading || providersLoading;

    return (
        <FlowShell
            onExit={handleExit}
            onBack={handleBack}
            primaryLabel="Add to practice"
            primaryDisabled={addDisabled}
            onPrimary={handleAddToPractice}
        >
            <Spin spinning={pageLoading}>
                <AddProvidersStep
                    title="Add providers"
                    subtitle="Add each new provider by name and email, then complete their credentialing details. Your existing providers aren't affected."
                    roster={roster}
                    onRefetch={refetchCredentialProviders}
                    showPayersCard
                    initialPayers={enrollmentData?.payers || []}
                />
            </Spin>
        </FlowShell>
    );
};

export default AddCredentialingProvidersPage;
